import * as _ from 'lodash';
import { ResponseContext } from './ResponseContext';
import { TromanError } from '../../common/TromanError';
import { checkResponseErrors, makeRequestInit } from '../../common/fetchHelper';
import type { IPromise } from "angular";

export type IApiPromise<T> = IPromise<T>;

interface IResponseData {
    Result: any;
    Objects: any[];
    Warnings: any[];
}

type ResultParser = (result: any, ctx: ResponseContext) => any;

export class ApiResponse {

    result: any;
    context: ResponseContext;
    warnings: any[];

    constructor(data: IResponseData) {
        if (data == null) throw new TromanError('api_invalid_response');

        this.result = data.Result;
        this.context = new ResponseContext(data.Objects || []);
        this.warnings = data.Warnings;
    }

    parseEntity(entityConstructor: any) {
        if (this.result == null) throw new TromanError('api_missing_result');

        if (_.isArray(this.result)) {
            return this.result.map(id => this.context.get(id, entityConstructor));
        } else {
            return this.context.get(this.result, entityConstructor);
        }
    }

    createEntityResponse(entityConstructor: any) {
        if (this.result == null) throw new TromanError('api_missing_result');

        return {
            result: this.parseEntity(entityConstructor),
            warnings: this.warnings,
        };
    }

    parseListResult(resultParser: ResultParser) {
        if (this.result == null) throw new TromanError('api_missing_result');
        if (!_.isArray(this.result)) throw new TromanError('api_result_not_array');

        return _.map(this.result, x => resultParser(x, this.context));
    }

    parseSingleResult(resultParser: ResultParser) {
        if (this.result == null) throw new TromanError('api_missing_result');

        return resultParser(this.result, this.context);
    }

    getResult() {
        if (this.result == null) throw new TromanError('api_missing_result');

        return this.result;
    }

    createEmptyResponse() {
        return {
            warnings: this.warnings,
        };
    }
}

function parseResponse(rsp: Response) {
    return rsp.json().then(data => new ApiResponse(data));
}

function voidResponse() {
}

function parseContentDispositionFilename(contentDisposition: string|null) {
    var re = /filename\*\s*=\s*UTF-8''([^;]*)/i;
    if (contentDisposition != null) {
        var m = re.exec(contentDisposition);
        if (m && m[1]) {
            return decodeURIComponent(m[1]).trim();
        }
    }

    throw new TromanError('parse_contentdisposition_filename');
}

export class DownloadResult {
    filename: string;
    blob: Blob;

    constructor(filename: string, blob: Blob) {
        this.filename = filename;
        this.blob = blob;
    }

    asDataUrl() {
        return new Promise((resolve, reject) => {
            let fr = new FileReader();
            fr.onload = evt => resolve(evt.target!.result); //TODO
            fr.onerror = evt => reject(evt.target!.error); //TODO
            fr.onabort = _evt => reject('aborted');
            fr.readAsDataURL(this.blob);
        });
    }
}

function handleDownloadResponse(rsp: Response) {
    let cd = rsp.headers.get('Content-Disposition');
    let fn = parseContentDispositionFilename(cd);
    return rsp.blob().then(blob => new DownloadResult(fn, blob));
}

export class ApiService {
    private _fetchWith: (url: string, init: RequestInit) => IApiPromise<any>;

    constructor(auth: any, baseurl: string) {
        this._fetchWith = function _fetchWith(url, init) {
            // Need to wrap using $q for angularjs change detection.
            return auth.fetch(baseurl + url, init).then(checkResponseErrors);
        };
    }

    authenticatedFetch(url: string, init: RequestInit) {
        return this._fetchWith(url, init);
    }
    
    getResponse(url: string) {
        const init = makeRequestInit('GET');
        return this._fetchWith(url, init).then(parseResponse);
    }

    postResponse(url: string, obj: any) {
        const init = makeRequestInit('POST', obj);
        return this._fetchWith(url, init).then(parseResponse);
    }

    postVoid(url: string, obj: any) {
        const init = makeRequestInit('POST', obj);
        return this._fetchWith(url, init).then(voidResponse);
    }

    putResponse(url: string, obj: any) {
        const init = makeRequestInit('PUT', obj);
        return this._fetchWith(url, init).then(parseResponse);
    }

    putVoid(url: string, obj: any) {
        const init = makeRequestInit('PUT', obj);
        return this._fetchWith(url, init).then(voidResponse);
    }

    postDownload(url: string, obj: any) {
        var init = makeRequestInit('POST', obj);
        return this._fetchWith(url, init).then(handleDownloadResponse);
    }

    getDownload(url: string) {
        const init = makeRequestInit('GET');
        return this._fetchWith(url, init).then(handleDownloadResponse);
    }
}
